import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, ButtonArea, Checkbox, CheckboxGroup, Dialog, Heading, IconButton, Textarea } from '../../../../../components/base';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { useUser } from '../../../../../libs/hooks/useUser';
import { Feedback, SurveyAnswer, SurveyAnswers } from '../../../../../libs/models/users/ChatUser';
import { useAlert } from '../../../../../context/AlertContext';
import { A11y, Autoplay, Navigation, Pagination } from 'swiper/modules';
import Image from '../../../../../components/Image';


const index = process.env.REACT_APP_SURVEY_INDEX ?? "1";

interface SurveyQuestion {
    questions: string,
    options: string,
    answers?: string[],
    etc: boolean,
}

const Surveys: Record<string, SurveyQuestion> = {
    "1": {
        questions: "Works AI에 어떤 기능이 더 추가되면 좋을까요?",
        answers: [
            "스마트 기능 (업무 브리핑, 메일 요약 등)이 더 강화되었으면 좋겠어요.",
            "메뉴를 쉽게 찾을 수 있었으면 좋겠어요.",
            "모바일에서도 업무수행 가이드, 검색 기능을 사용하고 싶어요.",
            "자주 사용하는 기능/메뉴를 메인화면에 배치하고 싶어요.",
            "가이드·FAQ·영상 등 사용 가이드 콘텐츠가 많아졌으면 좋겠어요."
        ],
        options: "radio",
        etc: true,
    }
}

interface SurveySliderProps {
    num: string;
    value: SurveyQuestion;
    onChange?: (id: string, value: SurveyAnswer) => void;
}

const SurveySlider: React.FC<SurveySliderProps> = ({ num, value, onChange }) => {
    const [result, setResult] = useState<SurveyAnswer>({answers:[], hasEtc:false, etc:''});
    // const result = useRef<SurveyAnswer>({answers:[], hasEtc:false, etc:''}).current;
    
    useEffect(() => {
        if (onChange) {
            onChange(num, result);
        }
    }, [result]); 

    const changeEtcState = (event: ChangeEvent<HTMLInputElement>) => {
        setResult(prev => ({
            ...prev,
            hasEtc: event.target.checked // 새 객체로 변경
          }));
    }

    const onChangeCheckbox = (selected: string[]) => {
        const selectedAnswer = value.answers?.filter((_value, index)=> selected?.includes(index.toString())) ?? [];
        setResult(prev => ({
            ...prev,
            answers: selectedAnswer // 새 객체로 변경
          }));
    }

    const onChangeEtc = (event: ChangeEvent<HTMLTextAreaElement>) => {
        result.etc = event.target.value;
        setResult(prev => ({
            ...prev,
            etc: event.target.value // 새 객체로 변경
          }));
    }

    return (
            
                <div className="tutorial-contents">
                    <div className="contents">
                        <div className="contents-image"><Image url="images/new_banner.gif"></Image></div>
                        <div className="contents-description">
                            <Heading size="title2">{value.questions}</Heading>
                        </div>
                        <div className='contents-checkbox'>
                            <CheckboxGroup
                                size='small' vertical options={
                                    value.answers?.map((answer, index) => ({ label: answer, value: index.toString() })) ?? []
                                }
                                onChange={(selectedValues) => { onChangeCheckbox(selectedValues) }}
                            >

                            </CheckboxGroup>
                            {value.etc &&
                                <Checkbox size='small' key={'etc'} className='checkbox-etc' onChange={changeEtcState}>
                                    <div className='checkbox-etc-label'>기타</div>
                                    <Textarea rows={1}
                                        placeholder={'기타 의견이 있으면 여기에 적어주세요.'}
                                        value={''}
                                        onChange={onChangeEtc}
                                        id='etc'
                                        readOnly = {!result.hasEtc}
                                    />
                                </Checkbox>
                            }
                        </div>
                    </div>
                </div>
            
    )
}

interface SurveySwiperProps {
    isOpen: boolean,
    onClose?: () => void;
}
export const SurveySwiper: React.FC<SurveySwiperProps> = ({ isOpen, onClose }) => {

    const [isFirstSlide, setIsFirstSlide] = useState(true);
    const [isLastSlide, setIsLastSlide] = useState(true);
    const [completeSurvey, setCompleteSurvey] = useState(true);
    const [feedback, setFeedback] = useState<Feedback>({ userId: '', rowKey: '', content: '', partitionKey: '' });
    const [survey, setSurvey] = useState<SurveyAnswers | null>(null);

    const { showAlert } = useAlert();

    const modules = [A11y, Autoplay, Navigation, Pagination];

    const user = useUser();

    const swiperRef = useRef<SwiperRef>(null);

    useEffect(() => {
        if (process.env.REACT_APP_SURVEY == 'TRUE' && isOpen) {
            user.getSurvey(index).then((result) => {
                setCompleteSurvey(result);
            }).catch(() => { })
        }
    }, [isOpen]);

    const saveFeedback = async () => {
        const result = await user.insertFeedback(feedback, survey);
        if (result) {
            showAlert({
                title: '제출 완료',
                message: '의견을 제출해 주셔서 감사합니다.',
                confirmLabel: '확인',
                showCancel: false,
                onConfirm: () => { if(onClose) onClose() },
            });
            setCompleteSurvey(true);
        } else {
            showAlert({
                title: '',
                message: '오류가 발생하였습니다. 다시 시도해주세요.',
                confirmLabel: '확인',
                showCancel: false,
            });
        }
    }

    const changeAnswer = (id: string, result: SurveyAnswer) => {
        const surveyAnswer = survey ? survey : { seq:index, userId:"", result:{}, partitionKey:index, rowKey: '' } as SurveyAnswers;
        const variable = Surveys[id];

        surveyAnswer.result[variable.questions] = result;
        setSurvey(surveyAnswer);
    }

    const changeFeedback = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const type = event.currentTarget.id;

        switch (type) {
            case 'feedback':
                setFeedback((c) => ({ ...c, content: event.target.value }));
                break;
            default:
                break;
        }
    }

    const onSurveyValidate = () => {
        const index = swiperRef.current?.swiper.realIndex ?? 0;
        const result = survey?.result[Surveys[index + 1].questions];
        if (!result || (result.answers.length == 0 && !result.hasEtc)) {
            showAlert({
                title: '',
                message: '보기 중에 최소 1개를 선택해주세요.',
                confirmLabel: '확인',
                showCancel: false,
            });
            return;
        }

        if (Surveys[index + 1].etc) {
            if (result.hasEtc && result.etc.trim().length == 0) {
                showAlert({
                    title: '',
                    message: '기타 의견을 작성해주세요.',
                    confirmLabel: '확인',
                    showCancel: false,
                });
                return;
            }
        }

        swiperRef.current?.swiper.slideNext();
    }

    


    return (
        <Dialog
            isOpen={isOpen}
            size="medium"
            buttons={
                <ButtonArea align="center">
                    {!isFirstSlide ?
                        <Button variant="outline" onClick={() => { swiperRef.current?.swiper.slidePrev();}}>이전</Button>
                        :
                        <></>}
                    {isLastSlide ?
                        <Button variant="primary" onClick={() => { void saveFeedback() }}>제출</Button> :
                        <Button variant="primary" onClick={onSurveyValidate}>다음</Button>
                    }
                </ButtonArea>
            }
            className="dialog-tutorial dialog-feedback"
            closeOnOutSide={completeSurvey}
            onClose={() => { if(onClose) onClose() }}
        >
            <div className="feedback-header dsx-Dialog-header">
                <Heading as="h2" size="title3" weight="semibold" className="title">
                    의견
                </Heading>
                {completeSurvey &&
                    <IconButton name="closeLarge" className="close" onClick={() => { if(onClose) onClose() }}>
                        닫기
                    </IconButton>}
            </div>
            <Swiper
                ref={swiperRef}
                modules={modules}
                loop={false}
                spaceBetween={24}
                slidesPerView={1}
                centeredSlides={true}
                allowTouchMove={false}
                pagination={{
                    clickable: false,
                }}
                initialSlide={-1}
                onSlideChange={(swiper) => {
                    const last = swiper.realIndex === swiper.slides.length - 1;
                    setIsLastSlide(last);

                    const first = swiper.realIndex == 0;
                    setIsFirstSlide(first);
                }}
            >
                {!completeSurvey &&  Object.entries(Surveys).map(([num, value]) => (
                    <SwiperSlide key={num}>
                        <SurveySlider num={num} value={value} onChange={changeAnswer}/>
                    </SwiperSlide>
                    ))}
                <SwiperSlide key={1} >
                    <div className="tutorial-contents">
                        <div className="contents">
                            <div className="contents-image"><Image url="images/new_banner.gif"></Image></div>
                            <div className="contents-description">
                                <Heading size="title2">더 나은 Works AI를 위해<br />
                                    <span className="description-emp">여러분의 의견</span>을 남겨주세요.</Heading>
                            </div>
                            <div className="contents-textarea">
                                <Textarea rows={5}
                                    placeholder={'의견 작성'}
                                    value={''}
                                    onChange={changeFeedback}
                                    id='feedback' />
                            </div>
                        </div>
                    </div>
                </SwiperSlide>
            </Swiper>
        </Dialog>
    )
}